/**
 * @voltxampere/engine — Delta-V damping (SPICE trust-region Newton)
 *
 * Sprint 7.1 diagnosed a 6-iteration limit cycle on PNP common-
 * emitter DC bias that `pnjlim` (Nagel 1975) alone could not break:
 *
 *     climb → climb → climb → BJT turns hard-on → linearised Newton
 *     step overshoots → V_E passes V_B into reverse bias → pnjlim
 *     stores the now-negative Vbe → exp(-Vbe/Vt) ≈ 0 → BJT off →
 *     iterate drops back to the cold-start fixed point → repeat.
 *
 * `pnjlim` is a ONE-SIDED limiter: it damps junction voltages that
 * are moving UP past `vcrit`, but passes downward moves through
 * unchanged. That's correct for the problem it was designed to
 * solve (keep `exp(V/Vt)` finite on the up-slope of the I-V knee)
 * but leaves the opposite direction unprotected. Delta-V damping is
 * the missing symmetric complement.
 *
 * **Trust-region formulation** (Sprint 8a v2): after some Sprint-8a
 * experimentation the naive per-node clamp turned out to destroy the
 * Newton direction: `V_B` and `V_E` are independently clipped, but
 * their *difference* `V_BE` can still swing by `2·LIMIT` per iter
 * because the clips cut the two components differently. That
 * reintroduces the Sprint 7.1 oscillation at a smaller amplitude and
 * the PNP CE smoke never converges.
 *
 * The robust algorithm is the SPICE `NIlimit`-style **uniform step
 * scaling**: compute one `alpha = LIMIT / max_junction_delta`,
 * then scale the WHOLE step vector so that no junction-bearing node
 * moves by more than `LIMIT` while every pair of junction voltages
 * keeps the LU-solver's ratio. Linear (non-junction) nodes get the
 * same `alpha` for free; the Newton direction is preserved. Once
 * the iterate is close to the operating point, `max_junction_delta`
 * drops below `LIMIT`, `alpha = 1`, and full Newton steps resume —
 * so quadratic convergence returns in the end-game.
 *
 * SPICE has used `maxDeltaV = 10·Vt ≈ 0.2585 V` as the step bound
 * for forty years. Nagel's 1975 thesis argues the choice from the
 * exponential's local Taylor radius (one order of magnitude in
 * current per 10·Vt — beyond that the linearised update is a poor
 * local approximation). We use the same constant.
 *
 * Policy:
 *
 *  - Applied AFTER the LU solve, BEFORE the convergence check. The
 *    scaled vector becomes the canonical `x_new` — the next
 *    iteration starts from it, and convergence compares |x_new-x|
 *    to tolerance using the scaled value.
 *
 *  - Only the step components of `junctionBearingIndices` are
 *    scaled — scaling the WHOLE step vector would also damp
 *    voltage-source-driven linear nodes, slowing rail supplies to
 *    a crawl (the MNA augmented row continues to re-assert
 *    `V = Vsource` every iteration and the damped delta never
 *    reaches the target). The scaling is uniform within the
 *    junction subset so that differential junction voltages like
 *    V_BE and V_EB keep the LU-solver's ratio. Linear nodes take
 *    the full Newton step.
 *
 *  - Empty `junctionBearingIndices` ⇒ short-circuit: no junction
 *    means no overshoot risk, alpha = 1. Linear / MOSFET-only
 *    circuits pay a copy + length check and nothing else.
 *
 *  - Returns a NEW Float64Array. `xNew` and `xOld` are never
 *    mutated. The function is idempotent.
 *
 *  - NaN/∞ passthrough. If any junction-bearing `xNew[i]` is non-
 *    finite we return the raw `xNew` unchanged; the Newton driver's
 *    `isFiniteVector` check aborts the whole solve on that step.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
import { VT_27C } from "../stamps/nonlinear/physical-constants.js";
/**
 * SPICE-standard Newton step bound for junction-bearing nodes:
 * ten thermal voltages at T = 300.15 K (≈ 0.2585 V).
 *
 * Rationale (Nagel 1975 §3.2, re-derived in the Sprint 8a report):
 * the junction companion linearisation error scales as
 * `|ΔV|² · (d²Id/dV²)`; setting `ΔV = 10·Vt` caps that error at one
 * order of magnitude in current per iteration — empirically the
 * sweet spot between under-damped oscillation (ΔV → ∞) and crawling
 * convergence (ΔV → 0).
 */
export const DELTA_V_LIMIT = 10 * VT_27C;
/**
 * Sprint 8a.1 smooth-regime threshold. When every junction-bearing
 * node's `|ΔV|` for this iteration is below `2·Vt` (≈ 0.052 V), the
 * iterate is inside the Newton trust region and damping is a pure
 * no-op: the helper short-circuits, reports `alpha = 1`, and the
 * Newton driver counts zero clamp events for that iteration.
 *
 * The constant prevents the Sprint 8a regression where NPN CE
 * ballooned from 8 to 12 iterations because the trust-region check
 * kept firing on small post-turn-on steps. Below `2·Vt` the
 * exponential is locally linear to first-order so there is no
 * overshoot risk to protect against.
 *
 * `2·Vt` matches pnjlim's own passthrough threshold
 * (`Math.abs(vnew - vold) <= 2·Vt`) — keeping the two layers'
 * "nothing to do" bands aligned is deliberate.
 */
export const DELTA_V_SMOOTH_THRESHOLD = 2 * VT_27C;
/**
 * Trust-region step scaling for Newton iterates on junction-bearing
 * circuits.
 *
 * Computes `alpha = min(1, limit / max_delta)` where `max_delta` is
 * the largest `|xNew[i] - xOld[i]|` over `i ∈ junctionBearingIndices`,
 * then returns `xOld + alpha · (xNew - xOld)` as a fresh
 * `Float64Array`. Because `alpha` is uniform across all indices the
 * Newton direction is preserved: ratios between step components —
 * and therefore differential junction voltages like V_BE and V_EB —
 * are preserved too. That is the property the naive per-node
 * clamp cannot deliver (Sprint 7.1 oscillation cause).
 *
 * Contract:
 *
 *   - `xNew.length === xOld.length` (throws otherwise)
 *   - `limit > 0, finite` (throws otherwise)
 *   - Input arrays are never mutated; result is a fresh array
 *   - Empty `junctionBearingIndices` ⇒ `{ result: copy(xNew), alpha: 1, clampedNodes: [] }`
 *   - Non-finite `xNew[i]` on a junction-bearing index ⇒ return the
 *     raw copy untouched so the Newton driver can abort with
 *     `ConvergenceError`
 *   - Indices outside `[0, length)` are ignored (defence-in-depth)
 *
 * @param xNew  Raw Newton iterate (output of `sparseSolve`).
 * @param xOld  Previous iterate (the `x` the solve started from).
 * @param junctionBearingIndices  Indices whose deltas govern `alpha`.
 * @param limit  Per-iteration junction step cap. Defaults to
 *               {@link DELTA_V_LIMIT} (10·Vt).
 */
export function applyDeltaVDamping(xNew, xOld, junctionBearingIndices, limit = DELTA_V_LIMIT, previousDeltas = null) {
    if (xNew.length !== xOld.length) {
        throw new ContractViolationError(`applyDeltaVDamping: xNew length ${String(xNew.length)} ≠ xOld length ${String(xOld.length)}`);
    }
    if (!Number.isFinite(limit) || limit <= 0) {
        throw new ContractViolationError(`applyDeltaVDamping: limit must be a positive finite number, got ${String(limit)}`);
    }
    if (previousDeltas !== null && previousDeltas.length !== xNew.length) {
        throw new ContractViolationError(`applyDeltaVDamping: previousDeltas length ${String(previousDeltas.length)} ≠ xNew length ${String(xNew.length)}`);
    }
    // No junctions → trivial no-op. Still copy so the caller can rely
    // on "result is always a fresh array".
    if (junctionBearingIndices.size === 0) {
        return {
            result: new Float64Array(xNew),
            alpha: 1,
            clampedNodes: [],
            junctionDeltas: new Float64Array(xNew.length),
        };
    }
    // Scan junction-bearing indices to find the largest |delta|, flag
    // which ones exceed the cap, and record the signed delta for
    // Sprint 8a.1 direction tracking. Any sign flip vs `previousDeltas`
    // means an oscillation signature — damping is warranted. Monotonic
    // climb (no sign flips) means Newton is converging cleanly and
    // damping would just slow it down (the Sprint 8a NPN regression).
    let maxDelta = 0;
    const clamped = [];
    const junctionDeltas = new Float64Array(xNew.length);
    let signFlipSeen = previousDeltas === null; // first iter: assume unknown → damp conservatively
    for (const i of junctionBearingIndices) {
        if (i < 0 || i >= xNew.length)
            continue;
        const vNew = xNew[i] ?? 0;
        const vOld = xOld[i] ?? 0;
        if (!Number.isFinite(vNew) || !Number.isFinite(vOld)) {
            // Poisoned iterate — return raw copy, let Newton abort.
            return {
                result: new Float64Array(xNew),
                alpha: 1,
                clampedNodes: [],
                junctionDeltas,
            };
        }
        const delta = vNew - vOld;
        const abs = Math.abs(delta);
        junctionDeltas[i] = delta;
        if (abs > maxDelta)
            maxDelta = abs;
        if (abs > limit)
            clamped.push(i);
        if (previousDeltas !== null && !signFlipSeen) {
            const prev = previousDeltas[i] ?? 0;
            // Sign flip ⇒ `prev * delta < 0`, guarded by threshold so that
            // cold-start `prev = 0` does not count as a flip.
            if (prev * delta < 0 &&
                Math.abs(prev) > DELTA_V_SMOOTH_THRESHOLD &&
                abs > DELTA_V_SMOOTH_THRESHOLD) {
                signFlipSeen = true;
            }
        }
    }
    // Sprint 8a.1: smooth-regime short-circuit. When every junction
    // moves less than `2·Vt`, Newton is converging quadratically and
    // the trust-region helper has nothing to damp. The short-circuit
    // matches `pnjlim`'s own passthrough band (`|vnew - vold| ≤ 2·Vt`,
    // see `pnjlim.ts:85`) — keeping the two layers' "nothing to do"
    // bands aligned avoids double-counting quiet iterations as clamp
    // events.
    if (maxDelta <= DELTA_V_SMOOTH_THRESHOLD) {
        return {
            result: new Float64Array(xNew),
            alpha: 1,
            clampedNodes: [],
            junctionDeltas,
        };
    }
    // Every junction step fits in the trust region → full Newton step.
    if (maxDelta <= limit) {
        return {
            result: new Float64Array(xNew),
            alpha: 1,
            clampedNodes: [],
            junctionDeltas,
        };
    }
    // Sprint 8a.1: monotonic-climb short-circuit. When the previous
    // iteration's junction deltas had the SAME sign as this one on
    // every junction-bearing index, Newton is making consistent
    // directional progress (NPN cold-start climb, diode turn-on knee)
    // and does not need trust-region protection. Damping would just
    // cap the climb and force extra iterations — the Sprint 8a NPN
    // regression (8 → 12 iter). Sign-flip damping engages ONLY when
    // the LU solve reverses direction, the true oscillation signal.
    //
    // First iteration (previousDeltas null) forces `signFlipSeen =
    // true` at entry, so the first Newton step always sees the
    // trust-region — correct cold-start behaviour.
    if (!signFlipSeen) {
        return {
            result: new Float64Array(xNew),
            alpha: 1,
            clampedNodes: [],
            junctionDeltas,
        };
    }
    // Scale the junction-bearing components UNIFORMLY so V_BE, V_EB,
    // and similar junction voltages keep the LU-solver's ratio. Non-
    // junction indices (voltage source values, linear node voltages)
    // take the full Newton step — damping them is harmful because the
    // MNA augmented rows re-assert them every iteration and a damped
    // delta would never reach the actual rail voltage.
    const alpha = limit / maxDelta;
    const out = new Float64Array(xNew);
    for (const i of junctionBearingIndices) {
        if (i < 0 || i >= xNew.length)
            continue;
        const vOld = xOld[i] ?? 0;
        const vNew = xNew[i] ?? 0;
        out[i] = vOld + alpha * (vNew - vOld);
    }
    return { result: out, alpha, clampedNodes: clamped, junctionDeltas };
}
//# sourceMappingURL=delta-v-damping.js.map